import { computed, inject, Injectable, signal } from '@angular/core';
import { DroplistItem, PageTabsLayoutPageService } from '@eforall/dashboard';
import { EngagementGridContainerPage, EngagementType, StaffRoleId } from '@interfaces';
import { AcceleratorAreaService } from '../accelerator-area.service';


/**
 * The list of tabs on this page
 */
export enum AccCohortsPageTabId {
	Tab1 = 1,
	Tab2 = 2,
}

/**
 * The list of main tab droplist views on this page
 */
export enum AccCohortPageViewId {
	Teams = 1,
	Ventures = 2,
	Entrepreneurs = 3,
	Mentors = 4,
	Specialists = 5,
	OpenEngagements = 6,
	CompletedEngagements = 7,
	ScheduledFollowUps = 8,
	GoalsAndProgress = 9,
}


@Injectable({ providedIn: 'root' })
export class AccCohortPageService extends PageTabsLayoutPageService<AccCohortsPageTabId, AccCohortPageViewId> {

	public readonly areaService = inject(AcceleratorAreaService);

	public engagements = computed(() => this.areaService.data()?.engagements || []);
	public followUps = computed(() => this.areaService.data()?.followUps || []);
	public contactRequests = computed(() => this.areaService.data()?.contactRequests || []);
	public readonly page = signal<EngagementGridContainerPage>(EngagementGridContainerPage.Delivery);
	public engagementType = signal<EngagementType | undefined>(undefined);
	public readonly staffRoleId = signal<StaffRoleId | undefined>(StaffRoleId.ProgramFacilitator);
	private static readonly _viewDroplistItems: DroplistItem<never, AccCohortPageViewId>[] = [
		{
			uniqueId: AccCohortPageViewId.Teams,
			iconContext: {
				iconClass: 'fa-people-group',
				iconStyle: 'fa-solid',
				text: 'Teams',
				hideText: 'never',
			},
			groupName: 'People',
			customSort: '1-Views-1'
		},
		{
			uniqueId: AccCohortPageViewId.Ventures,
			iconContext: {
				iconClass: 'fa-user-group',
				iconStyle: 'fa-solid',
				text: 'Ventures',
				hideText: 'never',
			},
			groupName: 'People',
			customSort: '1-Views-2'
		},
		{
			uniqueId: AccCohortPageViewId.Entrepreneurs,
			iconContext: {
				iconClass: 'fa-user-vneck',
				iconStyle: 'fa-solid',
				text: 'Entrepreneurs',
				hideText: 'never',
			},
			groupName: 'People',
			customSort: '1-Views-3'
		},
		{
			uniqueId: AccCohortPageViewId.Mentors,
			iconContext: {
				iconClass: 'fa-user-tie',
				iconStyle: 'fa-solid',
				text: 'Mentors',
				hideText: 'never',
			},
			groupName: 'People',
			customSort: '1-Views-4'
		},
		{
			uniqueId: AccCohortPageViewId.Specialists,
			iconContext: {
				iconClass: 'fa-person-chalkboard',
				iconStyle: 'fa-solid',
				text: 'Specialists',
				hideText: 'never',
			},
			groupName: 'People',
			customSort: '1-Views-5'
		},
		{
			uniqueId: AccCohortPageViewId.OpenEngagements,
			iconContext: {
				iconClass: 'fa-message-arrow-up-right',
				iconStyle: 'fa-solid',
				text: 'Open Engagements',
				hideText: 'never',
			},
			groupName: 'Engagements',
			customSort: '1-Views-6'
		},
		{
			uniqueId: AccCohortPageViewId.CompletedEngagements,
			iconContext: {
				iconClass: 'fa-message-check',
				iconStyle: 'fa-solid',
				text: 'Completed Engagements',
				hideText: 'never',
			},
			groupName: 'Engagements',
			customSort: '1-Views-7'
		},
		{
			uniqueId: AccCohortPageViewId.ScheduledFollowUps,
			iconContext: {
				iconClass: 'fa-reply-clock',
				iconStyle: 'fa-solid',
				text: 'Scheduled Follow Ups',
				hideText: 'never',
			},
			groupName: 'Engagements',
			customSort: '1-Views-8'
		},
		{
			uniqueId: AccCohortPageViewId.GoalsAndProgress,
			iconContext: {
				iconClass: 'fa-chart-mixed',
				iconStyle: 'fa-solid',
				text: 'Goals and Progress',
				hideText: 'never',
			},
			groupName: 'Goals and Progress',
			customSort: '1-Views-9'
		}
	];

	constructor() {
		super(AccCohortPageService._viewDroplistItems, AccCohortPageService.name, AccCohortsPageTabId.Tab1, AccCohortPageViewId.Teams);
	}

	public get viewId() {
		return AccCohortPageViewId;
	}


	async reloadData() {
		await this.areaService.reloadData();
	}
}
